import { useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { parseBoldRuns, parseTooltipSegments, trimAroundPills } from './tooltipFormula'

const VIEWPORT_MARGIN = 8
// Both panels, floating and inline. Border: letter 99, 7.3.1.
const PANEL_STYLE = { boxShadow: '0 0 10px rgba(0,0,0,0.1)', border: '1px solid #9FB497' }
const ANCHOR_GAP = 2

// Viewport coordinates for a `floating` panel: left-aligned under its anchor, shifted left to stay
// on-screen, flipped above when it would run past the bottom -- see README.md ("Metric tooltips
// float above the strip's overflow wrapper").
export function placeFloatingPanel(anchor, panel, viewport, margin = VIEWPORT_MARGIN, gap = ANCHOR_GAP) {
  const maxLeft = viewport.width - panel.width - margin
  const left = Math.max(margin, Math.min(anchor.left, maxLeft))
  const below = anchor.bottom + gap
  if (below + panel.height <= viewport.height - margin) return { left, top: below }
  const above = anchor.top - gap - panel.height
  if (above >= margin) return { left, top: above }
  return { left, top: Math.max(margin, viewport.height - margin - panel.height) }
}

function overlaps(a, b) {
  return a.right > b.left && a.left < b.right && a.bottom > b.top && a.top < b.bottom
}

// Nearest ancestor that clips (the strip's overflow-x row), so a card scrolled fully out of it
// hides its panel instead of pointing at nothing.
function clipRectOf(el) {
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const { overflowX, overflowY } = window.getComputedStyle(node)
    if (overflowX !== 'visible' || overflowY !== 'visible') return node.getBoundingClientRect()
  }
  return null
}

// false = anchor fully off-screen or scrolled fully out of its clipping row.
export function anchorIsVisible(anchor, viewport, clip) {
  const screen = { left: 0, top: 0, right: viewport.width, bottom: viewport.height }
  return overlaps(anchor, screen) && (clip == null || overlaps(anchor, clip))
}

// Glyph bbox 9.88x9.83 inside a derived 16x16 frame, stroke #8A9A85 by default (spec section 1.6).
// The caller picks both the resting and the hover stroke -- see README.md ("Info-icon hover") for
// why DealScoreBlock passes the same value for both instead of the shared #328C12 default.
function Glyph({ stroke }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="4.94" stroke={stroke} />
      <path d="M8 7.2V11" stroke={stroke} strokeLinecap="round" />
      <circle cx="8" cy="5.3" r="0.6" fill={stroke} />
    </svg>
  )
}

// Panel geometry is spec 4.5; anchoring and the pill's own sizing are not -- see README.md ("the
// info tooltip's placement" and "tooltip formula pills"). Each bracket line renders as its own row
// (README.md, "tooltip formula pills" -- per-line, not per-bracket) so parallel labelled clauses
// stay symmetric instead of merging into one run.
// `first`/`last`: no outer margin at the panel's own padding edge; between pills the margins collapse.
function FormulaPill({ lines, first, last }) {
  return (
    <div
      className={`${first ? '' : 'mt-[16px]'} ${last ? '' : 'mb-[16px]'} rounded-[4px] bg-[#E9F0E5] px-[8px] py-[4px] leading-[20px]`}
      style={{ border: '0.5px solid #C6CFC6' }}
    >
      {lines.map((line, i) => (
        <div key={i}>
          <span className="font-bold text-brand-secondary">{line.bold}</span>
          <span className="text-brand-secondary">{line.plain}</span>
        </div>
      ))}
    </div>
  )
}

// Running text outside the pills; `**word**` runs are bold 700, same size and colour (letter 99, 7.3.5).
function TextRun({ content }) {
  return (
    <span>
      {parseBoldRuns(content).map((run, i) =>
        run.bold ? (
          <strong key={i} className="font-bold">
            {run.text}
          </strong>
        ) : (
          run.text
        )
      )}
    </span>
  )
}

// Hover/focus is local state, not a row-wide CSS `group` (the row also holds inputs a hover
// shouldn't trigger on). No `relative` on this span: `absolute left-0` must resolve against
// FieldRow's row, not this icon -- see FieldRow.jsx. A `<button>` out of the Tab order: Tab walks
// the input fields only (letter 111, CS#3 item 5) -- README.md ("Tab walks the fields only").
//
// hoverStroke recolours the glyph on the same predicate that gates the panel below (#328C12, Cong,
// letter 104 1.1.5; was #91AD85) -- see README.md ("Info-icon hover") for DealScoreBlock's opt-out.
//
// `floating` portals the panel out as position:fixed, for callers inside a clipping scroll row
// (MetricsStrip) -- README.md ("Metric tooltips float above the strip's overflow wrapper").
export default function FlipInfoIcon({ tooltip, stroke = '#8A9A85', hoverStroke = '#328C12', floating = false }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const triggerRef = useRef(null)
  const panelRef = useRef(null)
  const panelId = useId()
  const active = open && tooltip
  const floatingOpen = floating && Boolean(active)

  const close = () => {
    setOpen(false)
    setPos(null)
  }

  // Anchor is offsetParent -- the card the non-floating `absolute left-0 top-full` resolves against --
  // so the panel lands where it always did at >=1280. Re-placed on scroll/resize (one frame each),
  // hidden while the card is out of view, never closed: the strip's row scrolls under an open panel.
  useLayoutEffect(() => {
    if (!floatingOpen) return undefined
    let frame = null
    const place = () => {
      frame = null
      const trigger = triggerRef.current
      const panelEl = panelRef.current
      if (!trigger || !panelEl) return
      const anchorEl = trigger.offsetParent ?? trigger
      const anchor = anchorEl.getBoundingClientRect()
      const viewport = { width: window.innerWidth, height: window.innerHeight }
      if (!anchorIsVisible(anchor, viewport, clipRectOf(anchorEl))) {
        setPos({ left: 0, top: 0, hidden: true })
        return
      }
      setPos({ ...placeFloatingPanel(anchor, panelEl.getBoundingClientRect(), viewport), hidden: false })
    }
    const schedule = () => {
      if (frame == null) frame = window.requestAnimationFrame(place)
    }
    place()
    // Capture: scroll does not bubble, and the strip's own row is not the document.
    document.addEventListener('scroll', schedule, true)
    window.addEventListener('resize', schedule)
    return () => {
      if (frame != null) window.cancelAnimationFrame(frame)
      document.removeEventListener('scroll', schedule, true)
      window.removeEventListener('resize', schedule)
    }
  }, [floatingOpen])

  const segments = active ? trimAroundPills(parseTooltipSegments(tooltip)) : []
  const body = active
    ? segments.map((segment, i) =>
        segment.type === 'pill' ? (
          <FormulaPill key={i} lines={segment.lines} first={i === 0} last={i === segments.length - 1} />
        ) : (
          <TextRun key={i} content={segment.content} />
        )
      )
    : null

  return (
    <span className="inline-flex items-center">
      <button
        ref={triggerRef}
        type="button"
        tabIndex={-1}
        aria-label="More info"
        aria-describedby={active ? panelId : undefined}
        className="inline-flex items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-brand-green"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={close}
        onFocus={() => setOpen(true)}
        onBlur={close}
      >
        <Glyph stroke={active ? hoverStroke : stroke} />
      </button>
      {active && floating
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="tooltip"
              className="fixed z-30 w-[340px] max-w-[calc(100vw-16px)] rounded-[8px] bg-white p-[12px] text-[13px] leading-[20px] text-[#1A2A10] pointer-events-none whitespace-pre-line"
              style={{
                ...PANEL_STYLE,
                left: pos?.left ?? 0,
                top: pos?.top ?? 0,
                visibility: pos && !pos.hidden ? 'visible' : 'hidden',
              }}
            >
              {body}
            </div>,
            // #portal lives in public/index.html only; tests without it still get a body-level panel.
            document.getElementById('portal') ?? document.body
          )
        : null}
      {active && !floating ? (
        <div
          id={panelId}
          role="tooltip"
          // max-w subtracts this row's own fixed 48px offset from the viewport edge, not just a vw
          // fraction -- see README.md ("the tooltip's viewport-relative max-width").
          className="absolute z-30 left-0 top-full mt-[2px] w-[340px] max-w-[calc(100vw-56px)] rounded-[8px] bg-white p-[12px] text-[13px] leading-[20px] text-[#1A2A10] pointer-events-none whitespace-pre-line"
          style={PANEL_STYLE}
        >
          {body}
        </div>
      ) : null}
    </span>
  )
}

